import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {useDeletePatient} from '@/features/patients/services/useDeletePatient.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {DeleteModal} from '@/components/commons/delete-modal/DeleteModal'

interface DeletePatientModalProps {
    onClose: () => void
    onSuccess?: () => void
    patientId: string
    patientFullName: string
    canBeDeleted: boolean
}
export const DeletePatientModal: FC<DeletePatientModalProps> = ({
    patientId,
    patientFullName,
    canBeDeleted,
    onClose,
    onSuccess
}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const deleteMutation = useDeletePatient(patientId, {
        onError: errorHandler,
        onSuccess: async () => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            toast.success(t('commons:delete_success', {fullName: patientFullName}))
            onSuccess?.()
            onClose()
        }
    })

    return (
        <DeleteModal
            onClose={onClose}
            deleteMutation={deleteMutation}
            canBeDeleted={canBeDeleted}
            fullName={patientFullName}
            paragraph1={t('commons:delete_paragraph_1')}
            title={t('patients:delete_patient_title')}
            deniedTitle={t('patients:cannot_delete_patient_title')}
            deniedParagraph={
                <Trans
                    i18nKey="commons:cannot_delete_paragraph"
                    values={{fullName: patientFullName}}
                    components={{b: <b />}}
                />
            }
        />
    )
}
